import {
  Button,
  FormLabel,
  SvgIcon,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@Components/Loader';
import { CommonConstants, Icons } from '@Constants/index';
import { FileUploadResponseList } from '@Models/upload.models';
import uploadService from '@Services/upload.service';
import { setUploadData } from '@Slices/upload.slice';
import { AttachmentType } from '@Typings/emailUpload.types';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useAppDispatch, useAppSelector } from '@Utils/hooks/useStore.hook';

import { EmailFileUploadProps } from './EmailFileUpload.types';

/** Axios Cancel Token */
let cancelToken = axios.CancelToken.source();

const EmailFileUpload: React.FunctionComponent<EmailFileUploadProps> = (
  props: EmailFileUploadProps
): JSX.Element => {
  const {
    uploadBtnIcon,
    uploadBtnText,
    acceptedFormat,
    storeKey,
    uploadBtnIconProps,
    setAlertState,
  } = props;

  /** HOOKS */
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { triggerAlert } = useAlert();

  /** REDUX STATE */
  const uploadState = useAppSelector((state) => state.emailUpload);
  const { listOfAttachments } = uploadState;

  /** STATES */
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>('');

  const isUploading = uploadState[storeKey].isLoading;

  /** Returns the id for input element. */
  const id = `file-${storeKey}-${Math.floor(Math.random() * 1000)}`;

  /** Handles file uploading */
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files =
      event.target.files instanceof FileList
        ? event.target.files[0]
        : new File([''], 'emptyFile');

    const fileSize = files.size / 1000;
    const storeFileSize = listOfAttachments.data.length
      ? listOfAttachments.data[0].bytes / 1000
      : 0;

    /** Handling size validation and error message */
    if (fileSize + storeFileSize < CommonConstants.UPLOAD_FILE_SIZE) {
      setMessage('');
      setSelectedFile(files);
    } else {
      setMessage(t('common.uploads.size_error', { size: 1 }));
    }

    event.target.value = '';
  };

  /** Handles file uploading cancellation and deletion of uploaded file */
  const onUploadCancel = () => {
    cancelToken.cancel();
    // Getting new Cancel Token
    cancelToken = axios.CancelToken.source();
    setSelectedFile(null);
  };

  /** Handles file uploading */
  const handleUploading = useCallback(
    (file: File) => {
      const fileData = new FormData();
      fileData.append('file', file, file.name);

      dispatch(
        setUploadData({
          storeKey,
          attachment: {
            ...uploadState[storeKey],
            isLoading: true,
          },
        })
      );
      uploadService
        .uploadFile(fileData, cancelToken.token)
        .then(({ data, status }: AxiosResponse<FileUploadResponseList>) => {
          /** if statusCode is 200 means that the file is uploaded and we can set state accordingly */
          if (status === 200) {
            setSelectedFile(null);
            dispatch(
              setUploadData({
                storeKey,
                attachment: {
                  isLoading: false,
                  data: [
                    ...uploadState[storeKey].data,
                    {
                      filename: data[0].filename,
                      url: data[0].url,
                      bytes: data[0].bytes,
                    },
                  ],
                },
              })
            );
          }
        })
        .catch(() => {
          if (setAlertState) {
            triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
          }
          setSelectedFile(null);
          dispatch(
            setUploadData({
              storeKey,
              attachment: {
                ...uploadState[storeKey],
                isLoading: false,
              },
            })
          );
        });
    },
    [dispatch, storeKey, uploadState, triggerAlert, setAlertState]
  );

  useEffect(() => {
    if (selectedFile && !isUploading) {
      handleUploading(selectedFile);
    }
  }, [selectedFile, isUploading, handleUploading]);

  return (
    <Box display="flex" alignItems="center">
      <FormLabel htmlFor={id}>
        <input
          id={id}
          name={id}
          type="file"
          onChange={onChangeHandler}
          style={{ display: 'none' }}
          accept={acceptedFormat}
        />
        <Button
          variant="text"
          color="secondary"
          component="span"
          startIcon={
            !isUploading &&
            uploadBtnIcon && (
              <SvgIcon {...uploadBtnIconProps}>{Icons[uploadBtnIcon]}</SvgIcon>
            )
          }
          disabled={isUploading}
          sx={{ textTransform: 'none' }}
        >
          {isUploading ? (
            <>
              <Loader size="2.5rem" sx={{ mr: 0.8 }} />
              {storeKey === AttachmentType.Attachment
                ? t('common.uploads.uploading_file')
                : t('common.uploads.uploading_image')}
            </>
          ) : (
            uploadBtnText
          )}
        </Button>
      </FormLabel>
      {(isUploading || selectedFile) && (
        <IconButton onClick={onUploadCancel}>
          <SvgIcon>{Icons.close}</SvgIcon>
        </IconButton>
      )}
      {message && (
        <Typography color="error" variant="helpText">
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default EmailFileUpload;
